import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControl, InputLabel, Select, MenuItem, Box, Typography,
  List, ListItem, ListItemIcon, ListItemText, CircularProgress, Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { requirementsApi } from '../../services/api';

const OPERATIONS = [
  { value: 'review', label: 'Review requirements' },
  { value: 'tests', label: 'Generate test cases' },
  { value: 'entities', label: 'Identify entities' },
];

/**
 * BulkAiDialog (#114) — run an AI operation across all descendant requirements
 * of a container, showing per-item success/error. The backend caps the batch.
 */
const BulkAiDialog = ({ open, onClose, containerId, containerTitle }) => {
  const [operation, setOperation] = useState('review');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setError(null);
      setRunning(false);
    }
  }, [open]);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const data = await requirementsApi.bulkAiOperation(containerId, operation);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Bulk operation failed');
    } finally {
      setRunning(false);
    }
  };

  const successCount = result ? result.results.filter((r) => r.success).length : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Run AI on all items
        {containerTitle ? ` in "${containerTitle}"` : ''}
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth size="small" sx={{ mt: 1 }} disabled={running}>
          <InputLabel id="bulk-op-label">Operation</InputLabel>
          <Select
            labelId="bulk-op-label"
            label="Operation"
            value={operation}
            onChange={(e) => setOperation(e.target.value)}
          >
            {OPERATIONS.map((op) => (
              <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          Runs across all requirements below this container (capped per batch).
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {running && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Processing… this may take a moment.</Typography>
          </Box>
        )}

        {result && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {successCount}/{result.count} succeeded
            </Typography>
            <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
              {result.results.map((r) => (
                <ListItem key={r.id} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {r.success
                      ? <CheckCircleIcon color="success" fontSize="small" />
                      : <ErrorIcon color="error" fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${r.id} — ${r.title}`}
                    secondary={r.success ? null : r.error}
                    slotProps={{ primary: { variant: 'body2' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleRun} variant="contained" disabled={running || !containerId}>
          {running ? 'Running…' : 'Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BulkAiDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  containerId: PropTypes.string,
  containerTitle: PropTypes.string,
};

export default BulkAiDialog;
